import React, { useEffect } from "react";
import KeyboardEventHandler from "react-keyboard-event-handler";
import { arrayMove } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

// Sub-component imports
import ConfigurationButton from "./ConfigurationButton";
import OrderingPopup from "./OrderingPopup";

// Constants imports
import {
  FOCUS_TIMEOUT_VALUE,
  ZERO_VALUE,
  KEY_ESCAPE,
  KEY_R,
  OVERLAY_CLASS,
  MAIN_CONTAINER_CLASS,
  Z_INDEX_OPEN_FILTER,
  Z_INDEX_CLOSED_FILTER,
} from "./constants";

const OrderingComponent = ({
  table,
  showResetButton,
  setShowResetButton,
  setColumnOrder,
  columnOrder,
  resetColumnOrder,
  saveOrderingConfig,
  columnVisibilityKey,
  openFilter,
  setOpenFilter,
}) => {
  const { t: translate } = useTranslation();
  const mainButtonRef = React.useRef(null);

  const handleReset = () => {
    resetColumnOrder();
    setOpenFilter(false);
    setShowResetButton(false);
    localStorage.setItem(columnVisibilityKey, JSON.stringify({}));
    setTimeout(() => {
      mainButtonRef.current.focus();
    }, FOCUS_TIMEOUT_VALUE);
  };

  const handleToggleAll = () => {
    const currentColumnsVisibility = table.getIsAllColumnsVisible();
    table.toggleAllColumnsVisible();
    const columnVisibility = table.getAllLeafColumns().reduce((acc, column) => {
      const isPinned = column.getIsPinned ? column.getIsPinned() : false;
      acc[column.id] = isPinned ? true : !currentColumnsVisibility;
      return acc;
    }, {});
    saveOrderingConfig(null, columnVisibility);
  };

  const handleClickOutside = () => {
    if (openFilter) {
      setTimeout(() => {
        mainButtonRef.current.focus();
      }, FOCUS_TIMEOUT_VALUE);
    }
    setOpenFilter(false);
  };

  useEffect(() => {
    if (openFilter) {
      setTimeout(() => {
        mainButtonRef.current.focus();
      }, FOCUS_TIMEOUT_VALUE);
    }
  }, [openFilter]);

  const onKeyEvent = (key) => {
    if (key === KEY_ESCAPE) {
      handleClickOutside();
    }
    if (key === KEY_R) {
      handleReset();
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const columns = columnOrder;
      const oldIndex = columns.findIndex((column) => column === active.id);
      const newIndex = columns.findIndex((column) => column === over.id);
      const newColumns = arrayMove(columns, oldIndex, newIndex);
      setColumnOrder(newColumns);
      setShowResetButton(true);
    }
  };

  const hiddenColumns =
    table.getAllLeafColumns().length - table.getVisibleLeafColumns().length;
  const allLeafColumns = table.getAllLeafColumns().map((col) => col.id);

  return (
    <KeyboardEventHandler
      handleKeys={["esc", "y", "n", "r", "enter", "+", "shift+="]}
      onKeyEvent={(key) => onKeyEvent(key)}
    >
      {openFilter && (
        <div
          className={OVERLAY_CLASS}
          onClick={handleClickOutside}
        />
      )}
      <div
        className={clsx(
          MAIN_CONTAINER_CLASS,
          openFilter ? Z_INDEX_OPEN_FILTER : Z_INDEX_CLOSED_FILTER
        )}
      >
        <ConfigurationButton
          openFilter={openFilter}
          onClick={() => {
            openFilter ? handleClickOutside() : setOpenFilter(true);
          }}
          mainButtonRef={mainButtonRef}
          translate={translate}
          hiddenColumns={hiddenColumns}
        />
        {openFilter && (
          <OrderingPopup
            table={table}
            showResetButton={showResetButton}
            handleReset={handleReset}
            handleToggleAll={handleToggleAll}
            handleDragEnd={handleDragEnd}
            saveOrderingConfig={saveOrderingConfig}
            allLeafColumns={allLeafColumns}
            translate={translate}
          />
        )}
      </div>
    </KeyboardEventHandler>
  );
};

export default OrderingComponent;
